<template>
  <SearchFormByOrderType ref="searchFormRef" :searchController="searchController" :tableController="tableController" @export="exportStream" />

  <ProTable
    :columns="tableController.columns"
    :tableData="tableController.tableData"
    :loading="tableController.loading"
    :total="tableController.total"
    :pageState="tableController.pageState"
    @pageChange="tableController.onPageChange"
  />

  <!-- 模态 -->
  <LogDrawer :controller="useLogDrawerController" />
  <ApprModal ref="ApprModalRef" @success="tableController.refresh" />
</template>

<script>
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import SearchFormByOrderType from './orderMngConfig/SearchFormByOrderType/index.vue'
import SearchForm from '@/components/SearchForm'
import ProTable from '@/components/ProTable'
import FetchSelect from '@/components/FetchSelect'
import CustomLink from '@/components/customStyle/CustomLink'
import LogDrawer from '@/components/modal/LogDrawer'
import useLogDrawer from '@/components/modal/LogDrawer/useLogDrawer'
import useSearchFormCtrller from '@/hooks/useSearchFormCtrller'
import useTableController from '@/hooks/useTableController'
import usePageMeta from '@/hooks/router/usePageMeta'
import useExport from '@/hooks/useExport'
import request from '@/utils/request'
import { getDefaultSearchFormModel } from './orderMngConfig/func'
import { promptConfirm } from '@/utils/confirm'
import ApprModal from './ApprModal.vue'
import { ref } from 'vue'
import useElementRights from '@/hooks/useElementRights'

export default {
  name: '订单列表-共用',
  props: ['orderType'],
  setup() {
    const meta = usePageMeta()
    const router = useRouter()

    const ApprModalRef = ref(null)
    const getElementRights = useElementRights()

    // searchForm
    const searchController = useSearchFormCtrller({
      form: getDefaultSearchFormModel(meta),
    })

    const { setExportPostData, exportStream } = useExport('/web/system/doctororder/exportDoctorOrderList')

    // table
    const columns = [
      { label: '下单时间', prop: 'orderDateStr' },
      { label: '订单编号', prop: 'orderNo' },
      { label: '订单来源', prop: 'orderFromName', width: 130 },
      { label: '问诊类型', prop: 'orderTypeName', width: 120 },
      { label: '医生', prop: 'doctorName', width: 140 },
      { label: '医生集团', prop: 'orgName', width: 260 },
      { label: '患者姓名', prop: 'patientName', width: 100 },
      { label: '患者手机号', prop: 'patientPhoneNumber', width: 130 },
      { label: '金额(元)', prop: 'doctorFeeStr', width: 150 },
      { label: '订单状态', prop: 'orderStatusName', width: 110 },
      { label: '回访状态', prop: 'orderReturnVisitName', width: 90 },
      { label: '支付状态', prop: 'payStatusName', width: 110 },
      { label: '支付时间', prop: 'payDateStr' },
      { label: '完成时间', prop: 'finishDateStr' },
      {
        label: '操作',
        prop: 'actions',
        fixed: 'right',
        width: 180,
        render: (_, record) => {
          // 取消订单 (注:仅当 orderStatus = 0(诊疗单提交), 10(待支付), 40 待接诊, 50(咨询中)时,可操作)
          return (
            <div>
              <CustomLink onClick={() => handleDetail(record)}>详情</CustomLink>
              {getElementRights('cancelDoctorOrder') && ['0', '10', '40', '50'].includes(record.orderStatus) && (
                <CustomLink onClick={() => handleCancelOrder(record)}>取消订单</CustomLink>
              )}
              {['40', '50'].includes(record.orderStatus) && <CustomLink onClick={() => handleFinishOrder(record)}>完成</CustomLink>}
              {getElementRights('cancelDoctorOrderAdmin') && ['0', '10', '40', '50', '90'].includes(record.orderStatus) && (
                <CustomLink onClick={() => ApprModalRef.value?.open(record)}>退款</CustomLink>
              )}
              <CustomLink onClick={() => handleLog(record)}>订单日志</CustomLink>
            </div>
          )
        },
      },
    ]

    const tableController = useTableController({
      columns,
      ajax: ({ page, pageSize }) => {
        const { dateRange, ...values } = searchController.formModel
        const postData = {
          page,
          rows: pageSize,
          startDate: dateRange[0],
          endDate: dateRange[1],
          ...values,
          orderStatus: (() => {
            if (meta.type == 'undo') {
              // 40,50
              return values.orderStatus || '40,50'
            } else {
              return values.orderStatus
            }
          })(),
        }
        setExportPostData(postData)
        return request({
          url: '/web/system/doctororder/getDoctorOrderList',
          data: postData,
        }).then((data) => ({ list: data.data ?? [], totalNum: data.rowTop ?? 0 }))
      },
    })

    const useLogDrawerController = useLogDrawer({
      columns: [
        { title: '操作内容', dataIndex: 'logInfo', width: 'auto' },
        { title: '操作时间', dataIndex: 'logDateStr' },
      ],
      api: '/web/system/doctororder/getLogDoctorOrderOperList',
      getPostDataFunc: (openParams) => {
        return {
          orderNo: openParams.orderNo,
        }
      },
    })

    function handleDetail(record) {
      router.push(`/web/system/doctororder/ordermgr/detail?orderNo=${record.orderNo}`)
    }
    function handleCancelOrder(record) {
      promptConfirm({ label: '取消说明' }, (value) => {
        return request({
          url: '/web/system/doctororder/cancelDoctorOrder',
          data: {
            orderNo: record.orderNo,
            resultNote: value ?? '',
          },
        }).then(() => {
          tableController.refresh()
          ElMessage.success('取消操作成功')
        })
      })
    }
    // 完成咨询单
    function handleFinishOrder(record) {
      promptConfirm({ label: '完成说明' }, (value) => {
        return request({
          url: '/web/system/doctororder/finishDoctorOrder',
          data: {
            orderNo: record.orderNo,
            resultNote: value ?? '',
          },
        }).then(() => {
          tableController.refresh()
          ElMessage.success('操作成功')
        })
      })
    }
    function handleLog(record) {
      useLogDrawerController.open(record)
    }

    return {
      ApprModalRef,
      meta,
      searchController,
      tableController,
      exportStream,
      useLogDrawerController,
    }
  },
  // ...needKeepAliveMixin,
  components: { SearchForm, ProTable, FetchSelect, CustomLink, SearchFormByOrderType, LogDrawer, ApprModal },
}
</script>

<style lang="less">
@import url('./index_global.less');
</style>
